import React from "react";
import { cx } from "../../../style";
import "./Button.css";

/**
 * Button — базовая кнопка дизайн-системы.
 *
 * Идея: базовые стили заданы вариантом, а точечно всё переопределяется через props.
 *
 * @param {("primary"|"secondary"|"ghost"|"success"|"edit"|"delete"|"danger"|"custom")} [variant="primary"]
 *        Состояние/назначение кнопки. edit — редактирование, delete — удаление,
 *        custom — нейтральная база под свои цвета.
 * @param {("sm"|"md"|"lg")} [size="md"]  Размер.
 * @param {React.ReactNode} [icon]        Иконка слева (любой node: <FaEdit/>, <svg/>...).
 * @param {React.ReactNode} [iconRight]   Иконка справа.
 * @param {boolean} [loading=false]       Показать спиннер и заблокировать.
 * @param {boolean} [fullWidth=false]     Растянуть на всю ширину.
 * @param {string}  [title]               Нативный tooltip (полезно для icon-only).
 * @param {string}  [className]           Доп. классы.
 * @param {object}  [style]               Точечные inline-переопределения (цвета для custom и т.п.).
 * Остальные пропсы (onClick, type, disabled, aria-*) пробрасываются на <button>.
 */
const Button = React.forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    icon = null,
    iconRight = null,
    loading = false,
    fullWidth = false,
    disabled = false,
    type = "button",
    children,
    className,
    style,
    ...rest
  },
  ref,
) {
  const iconOnly = !children && (icon || iconRight);

  const classes = cx(
    "ui-btn",
    `ui-btn--${variant}`,
    `ui-btn--${size}`,
    fullWidth && "ui-btn--full",
    loading && "ui-btn--loading",
    iconOnly && "ui-btn--icon-only",
    className,
  );

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      style={style}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="ui-btn__spinner" aria-hidden="true" />
      ) : (
        icon && <span className="ui-btn__icon">{icon}</span>
      )}
      {children}
      {!loading && iconRight && <span className="ui-btn__icon">{iconRight}</span>}
    </button>
  );
});

export default Button;
